<template>
    <b-container id="sales-reps" v-if="$can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Sales Reps Management</h3>
                    </b-col>
                    <b-col cols="4" class="text-right">
                        <b-button variant="primary" @click="openCreateModal" class="btn-sm">
                            <i class="fas fa-plus"></i> New Sales Rep
                        </b-button>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table 
                    :items="salesReps" 
                    :fields="fields" 
                    :busy="isBusy"
                    hover 
                    responsive 
                    striped
                    :per-page="perPage"
                    :current-page="currentPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @filtered="onFiltered"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(name)="row">
                        <strong>{{ row.item.name }}</strong>
                    </template>


                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(usage_count)="row">
                        <b-badge 
                            :variant="row.item.usage_count > 0 ? 'info' : 'light'"
                            :class="{ 'clickable-badge': row.item.usage_count > 0 }"
                            @click="row.item.usage_count > 0 ? viewOrders(row.item) : null"
                            style="cursor: pointer;"
                        >
                            {{ row.item.usage_count }} order(s)
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="editSalesRep(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" @click="deleteSalesRep(row.item)" :disabled="row.item.usage_count > 0">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="sales-reps-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <!-- Create/Edit Modal -->
        <b-modal 
            :id="modalId" 
            :title="modalTitle" 
            @hidden="resetModal"
            @ok="saveSalesRep"
            :ok-disabled="!isFormValid"
        >
            <b-form @submit.prevent="saveSalesRep">
                <b-form-group label="Name" label-for="name" :invalid-feedback="errors.name">
                    <b-form-input 
                        id="name" 
                        v-model="form.name" 
                        :state="errors.name ? false : null"
                        required
                    ></b-form-input>
                </b-form-group>

                <!-- Location field hidden, always defaults to "a" -->

                <b-form-group>
                    <b-form-checkbox v-model="form.active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="saveSalesRep" :disabled="!isFormValid">
                    {{ isEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>

        <!-- Orders Modal -->
        <b-modal 
            id="orders-modal" 
            title="Sales Orders" 
            size="xl"
            @hidden="resetOrdersModal"
        >
            <div v-if="selectedSalesRep">
                <h6>Orders for: <strong>{{ selectedSalesRep.name }}</strong></h6>
                <hr>
            </div>

            <b-table 
                :items="orders" 
                :fields="orderFields" 
                :busy="ordersBusy"
                hover 
                responsive 
                striped
                :per-page="ordersPerPage"
                :current-page="ordersCurrentPage"
            >
                <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                </template>

                <template v-slot:cell(sales_order_number)="row">
                    <a 
                        :href="`/orders/sales-orders/details/${row.item.sales_order_number}`" 
                        target="_blank" 
                        class="text-decoration-none"
                    >
                        {{ row.item.sales_order_number }}
                    </a>
                </template>

                <template v-slot:cell(customer)="row">
                    <span v-if="row.item.customer">{{ row.item.customer.name }}</span>
                    <span v-else class="text-muted">No Customer</span>
                </template>

                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusVariant(row.item.status)">
                        {{ row.item.status }}
                    </b-badge>
                </template>

                <template v-slot:cell(total)="row">
                    ${{ parseFloat(row.item.total).toFixed(2) }}
                </template>

                <template v-slot:cell(date)="row">
                    {{ formatDate(row.item.date) }}
                </template>
            </b-table>

            <b-pagination
                v-model="ordersCurrentPage"
                :total-rows="ordersTotalRows"
                :per-page="ordersPerPage"
                aria-controls="orders-table"
                class="mt-3"
            ></b-pagination>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeOrdersModal">Close</b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'SalesReps',
    
    data() {
        return {
            salesReps: [],
            isBusy: false,
            perPage: 10,
            currentPage: 1,
            totalRows: 0,
            sortBy: 'name',
            sortDesc: false,
            
            fields: [
                { key: 'name', label: 'Name', sortable: true },
                { key: 'active', label: 'Status', sortable: true },
                { key: 'usage_count', label: 'Usage', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            
            // Modal states
            modalId: 'sales-rep-modal',
            isEdit: false,
            
            // Form
            form: {
                id: null,
                name: '',
                location: 'a',
                active: true
            },
            
            errors: {},
            
            // Orders modal data
            orders: [],
            selectedSalesRep: null,
            ordersBusy: false,
            ordersPerPage: 10,
            ordersCurrentPage: 1,
            ordersTotalRows: 0,
            
            orderFields: [
                { key: 'sales_order_number', label: 'Order #', sortable: true },
                { key: 'customer', label: 'Customer', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'total', label: 'Total', sortable: true },
                { key: 'date', label: 'Date', sortable: true }
            ]
        }
    },
    
    computed: {
        modalTitle() {
            return this.isEdit ? 'Edit Sales Rep' : 'Create Sales Rep'
        },
        
        isFormValid() {
            return this.form.name.trim() !== ''
        }
    },
    
    methods: {
        async loadSalesReps() {
            this.isBusy = true
            try {
                const response = await axios.get('/api/company/sales-reps')
                if (response.data.status === 'success') {
                    this.salesReps = response.data.sales_reps
                    this.totalRows = this.salesReps.length
                }
            } catch (error) {
                this.showAlert('error', 'Error loading sales reps', error.response?.data?.message || 'Unknown error')
            } finally {
                this.isBusy = false
            }
        },
        
        openCreateModal() {
            this.isEdit = false
            this.resetForm()
            this.$bvModal.show(this.modalId)
        },
        
        editSalesRep(salesRep) {
            this.isEdit = true
            this.form = { 
                ...salesRep,
                active: Boolean(salesRep.active) // Convert 1/0 to true/false
            }
            this.$bvModal.show(this.modalId)
        },
        
        async saveSalesRep() {
            this.errors = {}
            
            try {
                const url = this.isEdit ? `/api/company/sales-reps/${this.form.id}` : '/api/company/sales-reps'
                const method = this.isEdit ? 'put' : 'post'
                
                const response = await axios[method](url, this.form)
                
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeModal()
                    this.loadSalesReps()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        async deleteSalesRep(salesRep) {
            if (salesRep.usage_count > 0) {
                this.showAlert('error', 'Cannot Delete', 'This sales rep is being used by sales orders')
                return
            }
            
            if (confirm(`Are you sure you want to delete "${salesRep.name}"?`)) {
                try {
                    const response = await axios.delete(`/api/company/sales-reps/${salesRep.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadSalesReps()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },
        
        resetForm() {
            this.form = {
                id: null,
                name: '',
                location: 'a',
                active: true
            }
            this.errors = {}
        },
        
        closeModal() {
            this.$bvModal.hide(this.modalId)
            this.resetForm()
        },
        
        resetModal() {
            this.resetForm()
        },
        
        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },
        
        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        },
        
        async viewOrders(salesRep) {
            this.selectedSalesRep = salesRep
            this.ordersBusy = true
            this.ordersCurrentPage = 1
            
            try {
                const response = await axios.get(`/api/company/sales-reps/${salesRep.id}/orders`)
                if (response.data.status === 'success') {
                    this.orders = response.data.orders
                    this.ordersTotalRows = this.orders.length
                    this.$bvModal.show('orders-modal')
                }
            } catch (error) {
                this.showAlert('error', 'Error loading orders', error.response?.data?.message || 'Unknown error')
            } finally {
                this.ordersBusy = false
            }
        },
        
        getStatusVariant(status) {
            const statusMap = {
                'pending': 'warning',
                'confirmed': 'info',
                'shipped': 'primary',
                'delivered': 'success',
                'cancelled': 'danger'
            }
            return statusMap[status?.toLowerCase()] || 'secondary'
        },
        
        formatDate(dateString) {
            if (!dateString) return 'N/A'
            const date = new Date(dateString)
            return date.toLocaleDateString()
        },
        
        closeOrdersModal() {
            this.$bvModal.hide('orders-modal')
        },
        
        resetOrdersModal() {
            this.orders = []
            this.selectedSalesRep = null
            this.ordersCurrentPage = 1
            this.ordersTotalRows = 0
        }
    },
    
    mounted() {
        this.loadSalesReps()
    }
}
</script>

<style scoped>
#sales-reps {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}

.table th {
    border-top: none;
}

.badge {
    font-size: 0.75em;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}

.clickable-badge {
    transition: all 0.2s ease;
}

.clickable-badge:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
</style>
